<template>
  <transition
    name="el-zoom-in-top"
    @after-leave="$emit('dodestroy')"
  >
    <div
      v-show="visible"
      :class="popperClass"
      class="el-time-panel el-popper"
    >
      <div
        v-if="$slots['time-header']"
        class="rt-xpc-time-panel-slot rt-xpc-time-panel-slot__header"
      >
        <slot name="time-header">
        </slot>
      </div>
      <div
        :class="{ 'has-seconds': showSeconds }"
        class="el-time-panel__content"
      >
        <time-spinner
          ref="spinner"
          :arrow-control="useArrow"
          :show-seconds="showSeconds"
          :am-pm-mode="amPmMode"
          :date="date"
          :stepper="stepper"
          @change="handleChange"
          @select-range="setSelectionRange"
        >
        </time-spinner>
      </div>

      <div
        v-if="$slots['time-footer']"
        class="rt-xpc-time-panel-slot rt-xpc-time-panel-slot__footer"
      >
        <slot name="time-footer"></slot>
      </div>

      <div class="rt-xpc-time-panel__footer el-time-panel__footer">
        <div class="rt-xpc-time-panel__now">
          <rt-button
            v-if="now"
            background
            size="small"
            @click="changeToNow"
          >
            {{ t('el.datepicker.now') }}
          </rt-button>
        </div>
        <div class="rt-xpc-time-panel__btn">
          <rt-button
            text
            @click="handleCancel"
          >
            {{ t('el.datepicker.cancel') }}
          </rt-button>
          <rt-button
            :class="{ confirm: !disabled }"
            text
            type="plain"
            @click="handleConfirm()"
          >
            {{ t('el.datepicker.confirm') }}
          </rt-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
import { limitTimeRange, isDate, clearMilliseconds, timeWithinRange } from 'element-ui/lib/utils/date-util';
import Locale from 'element-ui/lib/mixins/locale';
import TimeSpinner from '../basic/time-spinner';
import RtButton from 'vue-xpc/src/components/button/rtButton.vue';

export default {
  components: {
    TimeSpinner,
    RtButton
  },
  mixins: [Locale],

  props: {
    visible: Boolean,
    timeArrowControl: Boolean,
    now: Boolean,
    // 分钟 / 秒跨度
    stepper: [Number, Boolean]
  },

  data () {
    return {
      popperClass: '',
      format: 'HH:mm:ss',
      value: '',
      defaultValue: null,
      date: new Date(),
      oldValue: new Date(),
      selectableRange: [],
      selectionRange: [0, 2],
      disabled: false,
      arrowControl: false,
      needInitAdjust: true
    };
  },

  computed: {
    showSeconds () {
      return (this.format || '').indexOf('ss') !== -1;
    },
    useArrow () {
      return this.arrowControl || this.timeArrowControl || false;
    },
    amPmMode () {
      if ((this.format || '').indexOf('A') !== -1) return 'A';
      if ((this.format || '').indexOf('a') !== -1) return 'a';
      return '';
    },
    _spinner () {
      return this.$refs.spinner;
    }
  },

  watch: {
    visible (val) {
      if (val) {
        this.oldValue = this.value;
        this.$nextTick(() => {
          this._spinner.emitSelectRange('hours')
          if (this.oldValue) {
            this.changeToValue(this.oldValue)
          } else {
            this.changeToValue(new Date())
          }
        });
      } else {
        this.needInitAdjust = true;
      }
    },

    value (newVal) {
      let date;
      if (newVal instanceof Date) {
        date = limitTimeRange(newVal, this.selectableRange, this.format);
      } else if (!newVal) {
        date = this.defaultValue ? new Date(this.defaultValue) : new Date();
      }

      this.date = date;
      if (this.visible && this.needInitAdjust) {
        this.$nextTick(_ => this.adjustSpinners());
        this.needInitAdjust = false;
      }
    },

    selectableRange (val) {
      this._spinner.selectableRange = val;
    },

    defaultValue (val) {
      if (!isDate(this.value)) {
        this.date = val ? new Date(val) : new Date();
      }
    }
  },

  mounted () {
    this.$nextTick(() => this.handleConfirm(true, true));
    this.$emit('mounted');
  },

  methods: {
    changeToSpinnerValue (type, value) {
      this._spinner.adjustSpinner(type, value);
    },
    changeToValue (value) {
      if (this._spinner._computedMinutesStepper) {
        this.changeToSpinnerValue('hours', value.getHours())
        this.changeToStepperValue('minutes', value.getMinutes())
        this.changeToSpinnerValue('seconds', value.getSeconds())
      } else if (this._spinner._computedSecondStepper) {
        this.changeToSpinnerValue('hours', value.getHours())
        this.changeToSpinnerValue('minutes', value.getMinutes())
        this.changeToStepperValue('seconds', value.getSeconds())
      } else {
        this.changeToSpinnerValue('hours', value.getHours())
        this.changeToSpinnerValue('minutes', value.getMinutes())
        this.changeToSpinnerValue('seconds', value.getSeconds())
      }
    },
    changeToStepperValue (type, value) {
      const stepperValue = Math.floor(value / this._spinner._getStepper);
      this._spinner.adjustSpinner(type, stepperValue);
      this._spinner.modifyDateField(type, stepperValue);
    },
    changeToNow () {
      this.changeToSpinnerValue('hours', new Date().getHours())
      this.changeToSpinnerValue('minutes', new Date().getMinutes())
      this.changeToSpinnerValue('seconds', new Date().getSeconds())
    },

    handleCancel () {
      this.$emit('pick', this.oldValue, false);
      this.$emit('time-cancel', this.oldValue, false)
    },

    handleChange (date) {
      // this.visible avoids edge cases, when use scrolls during panel closing animation
      if (this.visible) {
        this.date = clearMilliseconds(date);
        // if date is out of range, do not emit
        if (this.isValidValue(this.date)) {
          let value = this.type === 'time' ? this.oldValue : this.date
          this.$emit('pick', value, true);
        }
      }
    },

    setSelectionRange (start, end) {
      this.$emit('select-range', start, end);
      this.selectionRange = [start, end];
    },

    handleConfirm (visible = false, first, val) {
      if (first) return;
      const date = clearMilliseconds(limitTimeRange(this.date, this.selectableRange, this.format));
      let value = val || date;
      this.value = value;
      this.$emit('pick', value, visible, first);
      this.$emit('time-confirm', value, first)
    },

    handleKeydown (event) {
      const keyCode = event.keyCode;
      const mapping = { 38: -1, 40: 1, 37: -1, 39: 1 };

      // Left or Right
      if (keyCode === 37 || keyCode === 39) {
        const step = mapping[keyCode];
        this.changeSelectionRange(step);
        event.preventDefault();
        return;
      }

      // Up or Down
      if (keyCode === 38 || keyCode === 40) {
        const step = mapping[keyCode];
        this._spinner.scrollDown(step);
        this.changeToValue(this.date)
        event.preventDefault();
      }
    },

    isValidValue (date) {
      return timeWithinRange(date, this.selectableRange, this.format);
    },

    adjustSpinners () {
      return this._spinner.adjustSpinners();
    },

    changeSelectionRange (step) {
      const list = [0, 3].concat(this.showSeconds ? [6] : []);
      const mapping = ['hours', 'minutes'].concat(this.showSeconds ? ['seconds'] : []);
      const index = list.indexOf(this.selectionRange[0]);
      const next = (index + step + list.length) % list.length;
      this._spinner.emitSelectRange(mapping[next]);
    }
  }
};
</script>



// WEBPACK FOOTER //
// time.vue?1dbdb2ff